<template>
    <div
        :id="id"
        class="input-toggle-container"
        @click="handleClick"
    >
        <div
            class="input-toggle-wrapper"
            :class="{ error: error, disabled }"
            data-testid="input-toggle"
        >
            <div
                v-if="type === 'button'"
                class="input-toggle-button"
                :class="{ checked: modelValue, disabled, error }"
            >
                <span v-html="effectiveLabel"></span>
            </div>

            <div
                v-if="type === 'switch'"
                class="input-switch"
                :class="{ checked: modelValue, disabled, error }"
            >
                <div class="switch"></div>
            </div>

            <div
                v-if="effectiveLabel && type === 'switch'"
                class="input-switch-label"
                :class="{ checked: modelValue, disabled, error }"
                v-html="effectiveLabel"
            ></div>

            <div
                v-if="effectiveLabel && type === 'label'"
                class="input-toggle-label"
                :class="{ checked: modelValue, disabled, error }"
                v-html="effectiveLabel"
            ></div>
        </div>

        <div
            v-if="error"
            style="margin-top: 3px"
        >
            <ErrorText :error="error" />
        </div>
    </div>
</template>


<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import marked from '../../utilities/marked'
import sanitize from 'widget-components/utilities/sanitize'
import ErrorText from 'widget-components/components/ErrorText'
import type { BlockPath } from 'widget-components/integrations/CustomPage/types'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id?: string
    modelValue?: boolean
    kind?: string
    disabled?: boolean
    error?: boolean
    label?: string
    selectedLabel?: string
    selected?: boolean
    blockPath?: BlockPath
}>()

const type = computed(() => {
    return props.kind?.toLowerCase() || 'label'
})

const emit = defineEmits(['update:modelValue'])

const checked = ref(props.modelValue)
const model = computed(() => props.modelValue)

const handleClick = () => {
    if (props.disabled) return
    checked.value = !checked.value
}

const effectiveLabel = computed(() => {
    if (props.selectedLabel && model.value) {
        return sanitize(marked(props.selectedLabel))
    }
    return sanitize(marked(props.label))
})

watch(model, (val) => {
    if (checked.value !== val) checked.value = val
})

watch(checked, (val) => {
    emit('update:modelValue', val)
})

onMounted(() => {
    if (props.selected) {
        checked.value = true
    }
})
</script>

<style scoped lang="sass">
@import './Toggle.sass'
</style>
